<template>
  <b-modal
    ref="modal"
    title="Course Details"
    scrollable
    @hidden="$emit('hidden')"
  >
    <div
      v-if="
        localConfig &&
        quizzesByCourse(courseId) &&
        quizzesByCourse(courseId).length > 0
      "
      class="px-2"
    >
      <!-- Fetch and show more quiz info here -->
      <b-form @submit.prevent="updateQuizConfig">
        <b-form-group label="Course start date & time">
          <date-picker
            v-model="courseOpenTime"
            :config="dateTimePickerConfig"
            class="w-100"
            @input="validateTimes('start')"
          />
          <small v-if="startTimeError" class="text-danger">{{
            startTimeError
          }}</small>
        </b-form-group>

        <b-form-group label="Course end date & time">
          <date-picker
            v-model="courseCloseTime"
            :config="dateTimePickerConfig"
            class="w-100"
            @input="validateTimes('end')"
          />
          <small v-if="endTimeError" class="text-danger">{{
            endTimeError
          }}</small>
        </b-form-group>

        <b-row>
          <b-col cols="12" sm="6">
            <b-form-group label="Attempts allowed">
              <b-form-input
                v-model="localConfig.attempts"
                type="number"
                min="0"
                @keydown="preventInvalidKeys"
              />
              <small v-if="localConfig.attempts == 0" class="text-muted">
                0 indicates infinite attempts.
              </small>
            </b-form-group>
          </b-col>

          <b-col cols="12" sm="6">
            <b-form-group label="Grade to Pass">
              <b-form-input
                v-model="localConfig.gradepass"
                type="number"
                min="0"
                @keydown="preventInvalidKeys"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group label="When time expires">
          <b-form-select
            v-model="localConfig.overduehandling"
            :options="overdueHandlingOptions"
          />
        </b-form-group>

        <b-row>
          <b-col cols="12" sm="6">
            <b-form-group>
              <div class="d-flex align-items-center h-100">
                <b-form-checkbox
                  v-model="localConfig.requiresafeexambrowser"
                  :value="1"
                  :unchecked-value="0"
                >
                  Enable Safe Exam Browser
                </b-form-checkbox>
              </div>
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
    </div>
    <div v-if="quizzesByCourse(courseId)?.length == 0">
      Either configurations or quizzes in this course are not available. Please
      try again later!
    </div>
    <template #modal-footer>
      <b-button class="btn" @click="hide">Close</b-button>
      <button
        v-if="
          localConfig &&
          quizzesByCourse(courseId) &&
          quizzesByCourse(courseId).length > 0
        "
        class="btn btn-cp"
        @click="updateQuizConfig"
        :disabled="!!startTimeError || !!endTimeError"
      >
        Update
      </button>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters } from "vuex";
import datePicker from "vue-bootstrap-datetimepicker";

export default {
  props: {
    courseId: {
      type: Number,
      default: null,
    },
    instanceId: {
      type: Number,
      default: 11,
    },
  },
  components: {
    datePicker,
  },
  data() {
    return {
      localConfig: null,
      courseOpenTime: null,
      courseCloseTime: null,
      startTimeError: null,
      endTimeError: null,
      overdueHandlingOptions: [
        {
          text: "Open attempts are submitted automatically",
          value: "autosubmit",
        },
        {
          text: "There is a grace period when open attempts can be submitted, but no more questions answered",
          value: "graceperiod",
        },
        {
          text: "Attempts must be submitted before time expires, or they are not counted",
          value: "autosubmit",
        },
      ],
      dateTimePickerConfig: {
        format: "YYYY-MM-DD HH:mm",
        showClose: true,
        showTodayButton: true,
        stepping: 1,
      },
    };
  },
  computed: {
    ...mapGetters({
      courses: "$_moodle_course/getCourses",
      config: "$_moodle_course/getConfigurations",
      quizzesByCourse: "$_moodle_course/getQuizzesByCourse",
    }),
  },
  watch: {
    config(newVal) {
      if (newVal) {
        this.localConfig = { ...newVal };

        const myCourse = this.courses.find(
          (course) => course.id == this.courseId
        );

        // Populate hour and minute from existing timestamp
        if (myCourse.startdate)
          this.courseOpenTime = new Date(myCourse.startdate * 1000);

        if (myCourse.enddate)
          this.courseCloseTime = new Date(myCourse.enddate * 1000);
      }
    },
  },
  methods: {
    show() {
      this.$refs.modal.show();
    },
    hide() {
      this.$refs.modal.hide();
    },
    preventInvalidKeys(event) {
      const input = event.target;
      const key = event.key;

      const invalidKeys = ["e", "E", "+", "-", "."];
      if (invalidKeys.includes(key)) {
        event.preventDefault();
      }

      // Prevent '0' as the first character
      if (
        key === "0" &&
        (input.value.length === 0 ||
          (input.selectionStart === 0 && !input.value.length))
      ) {
        event.preventDefault();
      }
    },
    updateQuizConfig() {
      if (this.courseOpenTime && this.courseCloseTime) {
        const open = new Date(this.courseOpenTime);
        const close = new Date(this.courseCloseTime);

        const isSameDate =
          open.getFullYear() === close.getFullYear() &&
          open.getMonth() === close.getMonth() &&
          open.getDate() === close.getDate();

        if (isSameDate) {
          this.localConfig.timeopen = Math.floor(open.getTime() / 1000);
          this.localConfig.timeclose = Math.floor(close.getTime() / 1000);
        } else {
          this.$store.dispatch("openSnackbar", {
            type: "danger",
            message: "Open and Close times must be on the same date.",
          });
          return;
        }
      } else {
        this.$store.dispatch("openSnackbar", {
          type: "danger",
          message: "Please set both Open and Close times.",
        });
        return;
      }

      const data = {
        instanceId: this.instanceId,
        courseId: this.courseId,
        settings: this.localConfig,
      };

      // dispatch update with full config
      this.$store.dispatch("$_moodle_course/updateCourseConfigurations", data);
      this.hide();
    },
    validateTimes(time) {
      this.startTimeError = null;
      this.endTimeError = null;

      if (this.courseOpenTime && this.courseCloseTime) {
        const start = new Date(this.courseOpenTime);
        const end = new Date(this.courseCloseTime);

        const isSameDate =
          start.getFullYear() === end.getFullYear() &&
          start.getMonth() === end.getMonth() &&
          start.getDate() === end.getDate();

        if (!isSameDate) {
          if (time == "start")
            this.startTimeError =
              "Course Start time must be on the same date as course end time.";
          else
            this.endTimeError =
              "Course End time must be on the same date as course end time.";
          return false;
        }

        if (start >= end) {
          if (time == "start")
            this.startTimeError =
              "Course Start time must be before course end time.";
          else
            this.endTimeError =
              "Course End time must be after course start time.";
          return false;
        }
      } else if (!this.courseOpenTime) {
        this.startTimeError = "Please set a valid course start time.";
        return false;
      } else if (!this.courseCloseTime) {
        this.endTimeError = "Please set a valid course end time.";
        return false;
      }

      return true;
    },
    validateTimeLimit() {
      this.timeLimitError = null;

      if (!this.courseOpenTime || !this.courseCloseTime || !this.timeValue)
        return true;

      const start = new Date(this.courseOpenTime).getTime();
      const end = new Date(this.courseCloseTime).getTime();
      const diffInSeconds = (end - start) / 1000;

      let limitInSeconds = 0;
      const value = parseInt(this.timeValue, 10);

      switch (this.timeUnit) {
        case "hours":
          limitInSeconds = value * 3600;
          break;
        case "minutes":
          limitInSeconds = value * 60;
          break;
        default:
          limitInSeconds = value;
      }

      if (limitInSeconds > diffInSeconds) {
        this.timeLimitError =
          "Time limit cannot exceed the duration between quiz open and close time.";
        return false;
      }

      return true;
    },
  },
};
</script>

<style scoped>
::v-deep .modal-header {
  padding: 0.75rem !important;
}

::v-deep .bootstrap-datetimepicker-widget {
  transform-origin: top left;
}

::v-deep .bootstrap-datetimepicker-widget .timepicker,
::v-deep .bootstrap-datetimepicker-widget .datepicker {
  font-size: 1rem;
}

::v-deep .bootstrap-datetimepicker-widget table td.active,
::v-deep .bootstrap-datetimepicker-widget table td.active:hover {
  background-color: #007bff;
  color: #fff !important;
}
</style>
